import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useStepFlow } from './useStepFlow';
import type {
  ReviewPick,
  ReviewSummaryEntry,
  UseReviewCardArgs,
  UseReviewCardResult,
} from '../types/review-types';
import { buildReviewCardCopy, EMPTY_REVIEW_CARD_COPY } from '../utils/reviewCardCopy';
import { buildReviewReply } from '../utils/reviewReply';

/**
 * State and copy for the Review card: the step flow over the turn's
 * questions and library matches, per-question free-text drafts, and the
 * open/closed state. Picks and drafts live as long as the card is mounted, so
 * closing and reopening keeps them; the host unmounts it once the researcher
 * sends any message.
 */
export const useReviewCard = ({
  items,
  onAnswer,
  disabled = false,
}: UseReviewCardArgs): UseReviewCardResult => {
  const intl = useIntl();
  // A new turn (a different first item) starts the card over.
  const resetKey = items[0]?.id ?? '';
  const flow = useStepFlow<ReviewPick>(
    items.map((item) => item.id),
    resetKey
  );
  const [isClosed, setIsClosed] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [seenKey, setSeenKey] = useState(resetKey);
  if (seenKey !== resetKey) {
    setSeenKey(resetKey);
    setIsClosed(false);
    setDrafts({});
  }

  const questionIds = useMemo(
    () => items.filter((item) => item.kind === 'question').map((item) => item.id),
    [items]
  );
  const matchCount = items.length - questionIds.length;
  const currentItem = items.find((item) => item.id === flow.currentId) ?? null;
  const questionNumber = currentItem ? questionIds.indexOf(currentItem.id) + 1 : 0;
  const isReview = flow.view.kind === 'review';
  const stepIndex = flow.view.kind === 'step' ? flow.view.index : items.length;

  const summaryEntries = useMemo<ReviewSummaryEntry[]>(
    () => items.map((item) => ({ itemId: item.id, item, pick: flow.picks[item.id] ?? null })),
    [items, flow.picks]
  );

  const onSelect = useCallback(
    (pick: ReviewPick) => {
      if (!disabled) flow.select(pick);
    },
    [disabled, flow]
  );

  const onDraftChange = useCallback((itemId: string, text: string) => {
    setDrafts((prev) => ({ ...prev, [itemId]: text }));
  }, []);

  // One reply answers the whole group; the first item carries the id the
  // transcript uses to mark the group answered.
  const onConfirm = useCallback(() => {
    if (disabled) return;
    const first = items[0];
    onAnswer({
      text: buildReviewReply(items, flow.picks),
      ...(first.kind === 'question' ? { questionId: first.id } : { checkpointId: first.id }),
    });
  }, [disabled, items, flow.picks, onAnswer]);

  // Nothing to say when there is nothing to review, so a host that always calls
  // this hook (the layout does, with the flag off) pays for no copy.
  const copy = useMemo(
    () =>
      items.length > 0
        ? buildReviewCardCopy(intl, {
            stepIndex,
            itemCount: items.length,
            questionCount: questionIds.length,
            matchCount,
          })
        : EMPTY_REVIEW_CARD_COPY,
    [intl, items.length, stepIndex, questionIds.length, matchCount]
  );

  return {
    isReview,
    isClosed,
    currentItem,
    currentPick: flow.currentPick,
    questionNumber,
    questionCount: questionIds.length,
    summaryEntries,
    drafts,
    ...copy,
    canGoBack: !disabled && flow.canGoBack,
    canGoNext: !disabled && flow.canGoNext,
    disabled,
    onBack: flow.goBack,
    onNext: flow.goNext,
    onSelect,
    onDraftChange,
    onClose: () => setIsClosed(true),
    onReopen: () => setIsClosed(false),
    onConfirm,
  };
};
